import { Button, P1 } from "components"
import React, { useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { Select, TableColumnsType, TableColumnType, Timeline } from "antd"
import { formatCurrencyNoSymbol } from "utils/currency"
import { useQuery } from "@tanstack/react-query"
import {
  ADMIN_PEOPLE_ENDPOINTS,
  adminPeopleService,
} from "services/adminPeopleService"
import { PAYROLL_ENDPOINTS, payrollService } from "services/payrollService"
import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import { EmployeePaygradeHistoryEntity, IEmployee } from "stores/employee/types"
import dayjs from "dayjs"
import getIdFromKey from "utils/getIdFromKey"
import { AppTable } from "components/new-stuff/app-table"

interface IPayslipYear {
  id: number
  text: string
}

interface IPayslipItem {
  payroll_key: string
  net_pay: string
  pay_date: string
  currency_symbol: string
}

interface IEmployeePayslips {
  person_id: string
  person_key: string
  first_name: string
  middle_name: string
  last_name: string
  full_name: string
  nick_name: string
  currency_symbol: string
  current_year: string
  payslips: IPayslipItem[]
  payslipYearToDate: {
    net_pay: string
    tax: string
    gross_pay: string
    currency_symbol: string
  }
}

const tabs = [
  {
    index: 0,
    title: "Payslips",
  },
  {
    index: 1,
    title: "Paygrade History",
  },
]

const CompensationHistory = () => {
  const [currentSubPage, setCurrentSubPage] = useState<number>(0)

  return (
    <>
      <div className="h-full flex justify-between items-center gap-6 px-4 md:px-6 lg:px-10 border-y border-y-neutral40 bg-white overflow-x-auto">
        <div className="h-full flex items-center gap-4 sm:gap-6">
          {tabs.map((item, index) => (
            <div
              className={` py-5 h-full relative flex items-center cursor-pointer`}
              onClick={() => setCurrentSubPage(item.index)}
              key={index}
            >
              <P1
                className={`leading-none ${
                  item.index === currentSubPage
                    ? "font-semibold text-primary500"
                    : "text-neutral100"
                }`}
              >
                {item.title}
              </P1>
              {item.index === currentSubPage && (
                <div className="w-full max absolute bottom-0 mt-5 h-[3px] bg-primary500" />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="w-full mx-auto flex flex-col gap-4">
        <div className="w-full bg-[#ededed]min-h-screen flex flex-col gap-10">
          <DashboardWrapper>
            <div className={currentSubPage === 0 ? "block" : "hidden"}>
              <EmployeePayslips />
            </div>
            <div className={currentSubPage === 1 ? "block" : "hidden"}>
              <PaygradeHistory />
            </div>
          </DashboardWrapper>
        </div>
      </div>
    </>
  )
}
export default CompensationHistory

const EmployeePayslips = () => {
  const currentYear = new Date().getFullYear()
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")
  const [selectedYear, setSelectedYear] = useState(currentYear)

  const navigate = useNavigate()

  const { isLoading: isPayslipLoading, data: dataRes } = useQuery<
    IBaseRes<IEmployeePayslips>
  >({
    queryKey: [ADMIN_PEOPLE_ENDPOINTS.VIEW_EMPLOYEE_PAYSLIPS, id, selectedYear],
    queryFn: () =>
      adminPeopleService.viewEmployeePayslips(id as string, selectedYear),
    enabled: !!selectedYear,
  })

  const data = dataRes?.data

  const { isLoading: isPayslipYearLoading, data: payslipYearsRes } = useQuery<
    IBaseRes<IPayslipYear[]>
  >({
    queryKey: [PAYROLL_ENDPOINTS.GET_PAYROLL_YEARS],
    queryFn: () => payrollService.getPayslipYears(),
  })

  const payslipYears = payslipYearsRes?.data

  const TableColumns: TableColumnsType<IPayslipItem> = [
    {
      title: "Pay Date",
      dataIndex: "pay_date",
      render: (payDate: string) => (
        <span className="whitespace-nowrap text-[#42526D] font-avenir text-sm">
          Paid on {payDate}
        </span>
      ),
    },
    {
      title: "Net pay",
      dataIndex: "net_pay",
      render: (netPay: string, record) => (
        <span className="whitespace-nowrap text-[#42526D] font-avenir text-sm">
          {record.currency_symbol} {formatCurrencyNoSymbol(Number(netPay))}
        </span>
      ),
    },

    {
      title: "Action",
      render: (_, record) => (
        <Link
          to={`/dashboard/employee/payslip/view?id=${record.payroll_key}&x=${data?.person_key}`}
        >
          <span
            className={`text-sm font-medium py-1 block text-primary500 text-center w-[80px] rounded-[4px]

            `}
          >
            View
          </span>
        </Link>
      ),
    },
  ]

  const handleChangePeriod = (value: any) => {
    let owner = value.split("-")
    let name = owner[0]
    owner = owner[1]

    setSelectedYear(owner)
  }
  return (
    <>
      <div className="bg-white  flex items-center justify-between gap-4 py-[10px] px-4">
        <Select
          showSearch={false}
          optionFilterProp="children"
          defaultValue={selectedYear && selectedYear}
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
          placeholder="Select Year"
          onChange={handleChangePeriod}
          optionLabelProp="label"
          options={payslipYears?.map((prd, index) => ({
            value: `${prd.text}-${prd.id}`,
            label: `${prd.text}`,
          }))}
          className="w-[120px]"
        />

        <div className="shrink-0">
          <Button
            onClick={() => {
              navigate(
                `/dashboard/employee/payslips/all?id=${selectedYear}&x=${id}`
              )
            }}
            color="primary"
          >
            <span className="">View All </span>
          </Button>
        </div>
      </div>
      <AppTable
        columns={TableColumns}
        dataSource={data?.payslips || []}
        scroll={{ x: true }}
        loading={isPayslipLoading}
        className="text-[#42526D]"
        rowKey="payroll_key"
      />
    </>
  )
}

const PaygradeHistory = () => {
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")

  const { isLoading: isLoadingCompensation, data: compensationRes } = useQuery<
    IBaseRes<IEmployee>
  >({
    queryKey: [ADMIN_PEOPLE_ENDPOINTS.VIEW_COMPENSATION_INFO, id],
    queryFn: () => adminPeopleService.getCompensationInfo({ id }),
  })

  const compensation = compensationRes?.data

  const TableColumns: TableColumnsType<EmployeePaygradeHistoryEntity> = [
    {
      title: "Paygrade/Payband",
      key: "description",
      render: (_, record) => record.description || record.alias,
    },
    {
      title: "Gross",
      children: [
        {
          title: "Annual",
          width: 120,
          render: (_, record) =>
            `${formatCurrencyNoSymbol(record.annual_gross)}`,
        },
        {
          title: "Monthly",
          width: 120,
          render: (_, record) =>
            `${formatCurrencyNoSymbol(record.monthly_gross)}`,
        },
      ],
    },
    {
      title: "Net",
      children: [
        {
          title: "Annual",
          width: 120,
          render: (_, record) => `${formatCurrencyNoSymbol(record.annual_net)}`,
        },
        {
          title: "Monthly",
          width: 120,
          render: (_, record) =>
            `${formatCurrencyNoSymbol(record.monthly_net)}`,
        },
      ],
    },
    {
      title: "Action By",
      dataIndex: "nick_name",
      key: "nick_name",
    },
    {
      title: "Effective Date",
      dataIndex: "start_date",
      key: "start_date",
      width: 150,
      render: (startDate: string) => {
        return startDate ? dayjs(startDate).format("DD MMM, YYYY") : ""
      },
    },
    {
      title: "Time Stamp",
      dataIndex: "time_stamp",
      key: "time_stamp",
      render: (timeStamp: string) => {
        return timeStamp
          ? dayjs(timeStamp).format("DD MMM, YYYY HH:mm:ss A")
          : ""
      },
    },
  ]

  return (
    <div className="f">
      <div>
        <AppTable
          columns={TableColumns}
          dataSource={compensation?.employeePaygradeHistory || []}
          loading={isLoadingCompensation}
          scroll={{
            y: "calc(100vh - 300px)",
            x: "max-content",
          }}
          tableLayout="auto"
        />
      </div>
    </div>
  )
}